import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTilt } from "../utils/useTilt";
import Logo1 from "../assets/imp.jpg";
import Logo2 from "../assets/dişestetiği.jpg";
import Logo3 from "../assets/kanal.jpg";
import Logo4 from "../assets/beyazlatma.jpg";
import Logo5 from "../assets/çck.jpg";
import Logo6 from "../assets/dişteli.jpg";
import Logo7 from "../assets/dişeti.jpg";
import Logo8 from "../assets/ortodonti.jpg";
import Logo9 from "../assets/bruksizm.jpg";

const logos = [Logo1, Logo2, Logo3, Logo4, Logo5, Logo6, Logo7, Logo8, Logo9];

// Kaynak fotoğrafların bazıları (implant vidası, kanal tedavisi, diş teli)
// siyah zemin üzerinde küçük ve ortada duruyor - kartın tamamını doldurmak
// yerine etrafta boşluk bırakıyor. Bu görsellerde ekstra bir taban zoom
// uyguluyoruz ki tüm kartlar tutarlı, "dolu" bir çerçeveyle görünsün.
// (Tailwind sınıfları burada literal string olarak yazılmalı - JIT taraması
// için, dinamik/template ile üretilen sınıf isimleri derlemede yakalanmaz.)
// Her satır: [taban zoom, hover zoom] - hover her zaman tabandan biraz daha
// büyük olacak şekilde ayrı ayrı tanımlanıyor (Tailwind'in hover sınıfı
// tabanı override ettiği için ikisini birlikte artan şekilde vermek gerekiyor).
const imageZoomClasses = [
  "scale-125 group-hover:scale-[1.35]", // İmplant Tedavisi (imp.jpg) - küçük obje, çok boşluk
  "group-hover:scale-110", // Estetik Diş Hekimliği - zaten kadraja iyi oturuyor
  "scale-[1.15] group-hover:scale-125", // Kanal Tedavisi (kanal.jpg) - orta boşluk
  "group-hover:scale-110", // Diş Beyazlatma - iyi oturuyor
  "group-hover:scale-110", // Çocuk Diş Hekimliği - iyi oturuyor
  "scale-125 group-hover:scale-[1.35]", // Diş Teli Tedavisi (dişteli.jpg) - küçük obje, çok boşluk
  "group-hover:scale-110", // Diş Eti Tedavisi - iyi oturuyor
  "scale-110 group-hover:scale-[1.2]", // Ortodonti - hafif boşluk
  "group-hover:scale-110", // Bruksizm Tedavisi - iyi oturuyor
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

interface ServiceCard {
  text: string;
  description: string;
  link: string;
}

const ServiceCardItem: React.FC<{ card: ServiceCard; image: string; zoomClass: string; detailsLabel: string }> = ({
  card,
  image,
  zoomClass,
  detailsLabel,
}) => {
  const { tilt, onMouseMove, onMouseLeave } = useTilt(6);

  return (
    // Dış katman: parent'ın stagger/scroll-reveal animasyonunu (fadeUp variant) devralır
    <motion.div variants={fadeUp} className="h-full">
      {/* İç katman: fareyle tilt efekti - ayrı bir "animate" hedefi olduğu için
          dış katmanın variant tabanlı giriş animasyonuyla çakışmaması için ayrıldı. */}
      <motion.div
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        animate={{ rotateX: tilt.rotateX, rotateY: tilt.rotateY }}
        transition={{ type: "spring", stiffness: 220, damping: 20 }}
        style={{ transformStyle: "preserve-3d", transformPerspective: 1000 }}
        className="group h-full flex flex-col bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300"
      >
        <div className="overflow-hidden">
          <img
            src={image}
            alt={card.text}
            loading="lazy"
            className={`w-full h-48 object-cover transition-transform duration-500 ${zoomClass}`}
          />
        </div>
        <div className="p-6 flex flex-col flex-1" style={{ transform: "translateZ(20px)" }}>
          <h3 className="text-xl font-semibold mb-3">{card.text}</h3>
          <p className="text-gray-700 mb-4 flex-1">{card.description}</p>
          <Link
            to={card.link}
            className="mt-auto text-yellow-600 font-semibold hover:text-yellow-700 transition inline-flex items-center gap-1 group/link"
          >
            {detailsLabel}
            <span className="transition-transform duration-300 group-hover/link:translate-x-1">
              &rarr;
            </span>
          </Link>
        </div>
      </motion.div>
    </motion.div>
  );
};

const CardsSection: React.FC = () => {
  const { t } = useTranslation();

  const cards = t("services.cards", { returnObjects: true }) as ServiceCard[];

  return (
    <section className="bg-amber-50/60 py-20">
      <div className="max-w-7xl mx-auto px-6">
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="text-yellow-600 font-semibold text-sm uppercase tracking-widest text-center mb-3"
      >
        {t("services.eyebrow")}
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-4xl font-bold mb-12 text-center"
      >
        {t("services.title")}
      </motion.h2>

      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
        transition={{ staggerChildren: 0.12 }}
      >
        {cards.map((card, idx) => (
          <ServiceCardItem
            key={idx}
            card={card}
            image={logos[idx]}
            zoomClass={imageZoomClasses[idx]}
            detailsLabel={t("services.details")}
          />
        ))}
      </motion.div>
      </div>
    </section>
  );
};

export default CardsSection;
